// src/components/Dashboard.jsx
// Dashboard Home — command centre: hero card, geo-risk alert, metrics, holdings, news
// Section 15 of the Nuvport OS Product Handbook

import { useState, useEffect, useMemo, useRef } from "react";
import { ModuleHeader } from "./UI";
import { calcGeoRiskScore } from "../utils/geoRisk";
import { saveSnapshot } from "../lib/portfolioSnapshots";
import PortfolioHistoryChart from "./dashboard/PortfolioHistoryChart";
import RiskScoreCard from "./dashboard/RiskScoreCard";
import MorningBrief from "./dashboard/MorningBrief";
import DegradedStateBanner from "./shared/DegradedStateBanner";
import DailyIntelligenceCard from "./dashboard/DailyIntelligenceCard";
import EmptyPortfolio from './empty/EmptyPortfolio';

function MetricCard({ label, value, sub, color = "var(--text-primary)", accent = false, tooltip, riskLabel }) {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div style={{
      background: accent ? "var(--accent-dim)" : "var(--surface)",
      border: `1px solid ${accent ? "var(--accent-mid)" : "var(--border)"}`,
      borderTop: "2px solid var(--accent)",
      borderRadius: "var(--radius-lg)",
      padding: "14px 16px",
      flex: 1,
      minWidth: 0,
      position: "relative",
    }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", marginBottom: 6, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4 }}>
        {label}
        {tooltip && (
          <span
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            style={{ cursor: "help", color: "var(--text-tertiary)", fontSize: 9, lineHeight: 1 }}
          >
            ⊙
          </span>
        )}
        {showTooltip && tooltip && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 100,
            background: "#0a0a0a", border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)", padding: "8px 10px",
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-secondary)",
            lineHeight: 1.5, maxWidth: 220, whiteSpace: "normal",
            boxShadow: "0 4px 16px rgba(0,0,0,.6)",
          }}>
            {tooltip}
          </div>
        )}
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 700, color, lineHeight: 1, display: "flex", alignItems: "center", gap: 8 }}>
        {value}
        {riskLabel && (
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700,
            color, background: `${color}18`,
            border: `1px solid ${color}35`,
            padding: "2px 6px", borderRadius: "var(--radius-sm)",
            letterSpacing: ".08em",
          }}>
            {riskLabel}
          </span>
        )}
      </div>
      {sub && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-secondary)", marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function QuickActionButton({ label, icon, onClick, desc, locked = false, onUnlock }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={locked ? undefined : onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov && !locked ? "var(--accent-dim)" : "var(--surface)",
        border: `1px solid ${hov && !locked ? "var(--accent-mid)" : "var(--border)"}`,
        borderRadius: "var(--radius-lg)",
        padding: "14px 16px",
        cursor: locked ? "default" : "pointer",
        textAlign: "left",
        transition: "all .15s",
        flex: 1,
        opacity: locked ? 0.4 : 1,
        position: "relative",
      }}
    >
      {locked && (
        <div style={{
          position: "absolute", top: 10, right: 10,
          fontSize: 12, color: "#ff6b35",
        }}>
          🔒
        </div>
      )}
      <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: hov && !locked ? "var(--accent)" : "var(--text-primary)", letterSpacing: ".06em", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: "var(--text-tertiary)", marginTop: 3 }}>
        {desc}
      </div>
      {locked && (
        <div
          onClick={e => { e.stopPropagation(); onUnlock?.(); }}
          style={{
            marginTop: 8,
            fontFamily: "var(--font-mono)", fontSize: 10,
            color: "#3ddc84", cursor: "pointer", letterSpacing: ".04em",
          }}
        >
          Add holdings →
        </div>
      )}
    </button>
  );
}

// Tiny sparkline using SVG
function Sparkline({ data = [], color = "var(--accent)", width = 100, height = 32 }) {
  if (!data.length) return null;
  const valid = data.filter(v => v != null && !isNaN(v));
  if (valid.length < 2) return null;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min || 1;
  const pts = valid.map((v, i) => {
    const x = (i / (valid.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// calcGeoRiskScore is imported from src/utils/geoRisk.js
// Both Dashboard and WorldMonitor consume the same function to guarantee
// identical scores across all modules.

function GeoRiskBadge({ score }) {
  const color = score >= 60 ? "var(--negative)" : score >= 30 ? "var(--caution)" : "var(--positive)";
  const label = score >= 60 ? "HIGH" : score >= 30 ? "ELEVATED" : "LOW";
  return (
    <span style={{
      fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
      color, background: `${color}15`,
      border: `1px solid ${color}30`,
      padding: "2px 8px", borderRadius: "var(--radius-sm)",
      letterSpacing: ".06em",
    }}>
      {score}/100 {label}
    </span>
  );
}

export default function Dashboard({ holdings = [], metrics, totalWeight, stockData, setActiveTab, settings, setSetting, geoEventBus, portfolioValue: portfolioValueProp, user, tier, onOpenBilling, forexPositions = [] }) {
  const [dismissed, setDismissed] = useState(false);
  const [time, setTime] = useState(new Date());
  const [flashedTickers, setFlashedTickers] = useState({});
  const [pvEditMode, setPVEditMode] = useState(false);
  const [pvEditRaw, setPVEditRaw] = useState("");
  const [pvFlash, setPVFlash] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const pvInputRef = useRef(null);

  const portfolioValue = portfolioValueProp ?? null;
  const portfolioReady = portfolioValue !== null && portfolioValue > 0;
  const compact = settings?.compactRows ?? false;

  function startPVEdit() {
    setPVEditRaw(portfolioValue != null ? String(portfolioValue) : "");
    setPVEditMode(true);
    setTimeout(() => pvInputRef.current?.focus(), 50);
  }

  async function confirmPVEdit() {
    const val = parseFloat(pvEditRaw.replace(/,/g, ""));
    if (!isNaN(val) && val > 0) {
      setSetting("portfolioValue", val);
      if (user?.id) saveSnapshot(user.id, val, "manual_update").catch(() => {});
      setPVFlash(true);
      setTimeout(() => setPVFlash(false), 2000);
    }
    setPVEditMode(false);
  }

  function cancelPVEdit() {
    setPVEditMode(false);
  }

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!geoEventBus) return;
    const unsub = geoEventBus.on('*', (evt) => {
      if (!evt.holdings_affected?.length) return;
      const expiresAt = Date.now() + 8000;
      setFlashedTickers(prev => {
        const next = { ...prev };
        evt.holdings_affected.forEach(ticker => {
          next[ticker] = { zone: evt.zone, expiresAt };
        });
        return next;
      });
      setTimeout(() => {
        setFlashedTickers(prev => {
          const next = { ...prev };
          evt.holdings_affected.forEach(ticker => {
            if (next[ticker]?.expiresAt <= Date.now()) delete next[ticker];
          });
          return next;
        });
      }, 8500);
    });
    return unsub;
  }, [geoEventBus]);

  const geoScore = useMemo(() => calcGeoRiskScore(holdings), [holdings]);

  // Whether any holding has a real share quantity — determines if we can
  // compute a real dollar value without a manually-set portfolioValue.
  const hasQuantityHoldings = holdings.some(h => (h.quantity ?? 0) > 0);

  const totalValue = useMemo(() => {
    if (!portfolioValue && !hasQuantityHoldings) return null;
    return holdings.reduce((sum, h) => {
      const price = h.livePrice ?? h.price ?? 0;
      const qty   = h.quantity ?? 0;
      if (qty) return sum + price * qty;
      if (!portfolioValue) return sum; // skip weight-based without a base
      return sum + portfolioValue * ((h.weight ?? 10) / 100);
    }, 0);
  }, [holdings, portfolioValue, hasQuantityHoldings]);

  const dayChange = useMemo(() => {
    if (!portfolioValue && !hasQuantityHoldings) return null;
    return holdings.reduce((sum, h) => {
      const change = h.liveChange ?? h.priceChange ?? 0;
      const qty    = h.quantity ?? 0;
      if (qty) return sum + change * qty;
      const price = h.livePrice || h.price;
      if (!price || !portfolioValue) return sum;
      return sum + change * (portfolioValue * ((h.weight ?? 10) / 100)) / price;
    }, 0);
  }, [holdings, portfolioValue, hasQuantityHoldings]);

  const prevCloseValue = totalValue != null && dayChange != null ? totalValue - dayChange : null;
  const dayChangePct = prevCloseValue != null && prevCloseValue > 0 ? (dayChange / prevCloseValue) * 100 : null;
  const isPositiveDay = (dayChangePct ?? 0) >= 0;

  const hasHoldings = holdings.length > 0;

  const sectorWeights = useMemo(() => {
    const map = {};
    holdings.forEach(h => {
      const s = h.sector ?? "Other";
      map[s] = (map[s] ?? 0) + (h.weight ?? 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [holdings]);

  return (
    <div className="fade-in">
      <style>{`@keyframes db-slide-down { from { transform: translateY(-8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <ModuleHeader
        visible={settings?.showModuleDescriptions ?? true}
        title="DASHBOARD"
        description="Your portfolio at a glance — value, today's gains or losses, risk level, and live news in one view."
        bullets={[
          "See your total portfolio value, how much you made or lost today, and your yearly return",
          "Monitor how exposed your holdings are to global instability and get real-time alerts",
          "Jump to any tool directly from the home screen",
        ]}
      />

      <DegradedStateBanner />

      {/* ── Daily Intelligence Card ───────────────────────────── */}
      <DailyIntelligenceCard
        userId={user?.id ?? null}
        holdings={holdings}
        portfolioReady={portfolioReady}
      />

      {/* ── Nuvport Risk Score Card ──────────────────────────── */}
      <RiskScoreCard
        userId={user?.id ?? null}
        holdings={holdings}
        portfolioReady={portfolioReady}
        tier={tier ?? "free"}
        onSeeWhatChanged={() => setBriefOpen(true)}
        onOpenBilling={onOpenBilling}
      />

      {/* ── Morning Brief slide-up panel ─────────────────────── */}
      {briefOpen && (
        <MorningBrief
          userId={user?.id ?? null}
          holdings={holdings}
          dayChange={dayChange}
          dayChangePct={dayChangePct}
          onClose={() => setBriefOpen(false)}
          onNavigate={(tab) => setActiveTab(tab)}
        />
      )}

      {/* ── Geo-risk alert strip ─────────────────────────────── */}
      {!dismissed && geoScore !== null && geoScore >= 30 && (
        <div style={{
          background: geoScore >= 60 ? "var(--negative-bg)" : "var(--caution-bg)",
          border: `1px solid ${geoScore >= 60 ? "rgba(255,92,92,0.25)" : "rgba(240,165,0,0.25)"}`,
          borderRadius: "var(--radius-lg)",
          padding: "10px 16px",
          display: "flex", alignItems: "center", gap: 12,
          marginBottom: 16,
          animation: "db-slide-down .25s ease",
        }}>
          <span style={{ fontSize: 14 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: geoScore >= 60 ? "var(--negative)" : "var(--caution)", letterSpacing: ".04em" }}>
              GLOBAL RISK ALERT
            </span>
            <span style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", marginLeft: 12 }}>
              Your holdings are {geoScore >= 60 ? "heavily" : ""} exposed to politically unstable regions — global risk score {geoScore}/100. Review your geographic exposure.
            </span>
          </div>
          <button
            onClick={() => setActiveTab("world")}
            style={{
              background: "transparent", border: `1px solid ${geoScore >= 60 ? "rgba(255,92,92,0.3)" : "rgba(240,165,0,0.3)"}`,
              color: geoScore >= 60 ? "var(--negative)" : "var(--caution)",
              padding: "5px 12px", borderRadius: "var(--radius-sm)",
              fontFamily: "var(--font-mono)", fontSize: 10, cursor: "pointer", letterSpacing: ".06em",
            }}
          >
            VIEW MAP
          </button>
          <button
            onClick={() => setDismissed(true)}
            style={{ background: "transparent", border: "none", color: "var(--text-tertiary)", cursor: "pointer", fontSize: 16 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Hero card ────────────────────────────────────────── */}
      <div style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: "24px 28px",
        marginBottom: 16,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".14em", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
            PORTFOLIO VALUE
            {!pvEditMode && (
              <span
                onClick={startPVEdit}
                title="Edit portfolio value"
                style={{ cursor: "pointer", opacity: 0.4, fontSize: 10, transition: "opacity .15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                onMouseLeave={e => e.currentTarget.style.opacity = "0.4"}
              >
                ✏
              </span>
            )}
            {pvFlash && (
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "#3ddc84", letterSpacing: ".06em" }}>
                ✓ Updated
              </span>
            )}
          </div>

          {pvEditMode ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 24, color: "#3ddc84" }}>$</span>
              <input
                ref={pvInputRef}
                type="text"
                inputMode="decimal"
                value={pvEditRaw}
                onChange={e => setPVEditRaw(e.target.value.replace(/[^0-9.,]/g, ""))}
                onKeyDown={e => { if (e.key === "Enter") confirmPVEdit(); if (e.key === "Escape") cancelPVEdit(); }}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: "2px solid #3ddc84",
                  outline: "none",
                  fontFamily: "var(--font-mono)",
                  fontSize: 32,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  width: 180,
                }}
              />
              <button onClick={confirmPVEdit} style={{ background: "none", border: "none", color: "#3ddc84", fontSize: 18, cursor: "pointer" }}>✓</button>
              <button onClick={cancelPVEdit} style={{ background: "none", border: "none", color: "var(--text-tertiary)", fontSize: 18, cursor: "pointer" }}>✗</button>
            </div>
          ) : totalValue != null ? (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 36, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1, marginBottom: 10 }}>
              ${totalValue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
          ) : hasHoldings ? (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 24, fontWeight: 700, color: "var(--text-tertiary)", lineHeight: 1, marginBottom: 6 }}>—</div>
              <button
                onClick={startPVEdit}
                style={{
                  background: "var(--accent-dim)", border: "1px solid var(--accent-mid)",
                  color: "var(--accent)", padding: "4px 12px",
                  borderRadius: "var(--radius-sm)", cursor: "pointer",
                  fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".06em",
                }}
              >
                SET PORTFOLIO VALUE →
              </button>
            </div>
          ) : (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 36, fontWeight: 700, color: "var(--text-tertiary)", lineHeight: 1, marginBottom: 10 }}>—</div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {dayChangePct != null ? (
              <span style={{
                fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700,
                color: isPositiveDay ? "var(--positive)" : "var(--negative)",
              }}>
                {isPositiveDay ? "+" : ""}{dayChangePct.toFixed(2)}% today
              </span>
            ) : hasHoldings ? null : (
              <span style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)" }}>
                Add holdings to see your portfolio value
              </span>
            )}
            {geoScore !== null && <GeoRiskBadge score={geoScore} />}
          </div>
        </div>

        {/* Sparkline if we have live prices */}
        {hasHoldings && holdings[0]?.sparkline?.length > 1 && (
          <div style={{ flexShrink: 0 }}>
            <Sparkline
              data={holdings[0].sparkline}
              color={isPositiveDay ? "var(--positive)" : "var(--negative)"}
              width={140}
              height={44}
            />
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 8, color: "var(--text-tertiary)", marginTop: 4, textAlign: "center" }}>
              30-DAY TREND
            </div>
          </div>
        )}

        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", textAlign: "right" }}>
          <div>{time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
          <div style={{ color: "var(--accent)", display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", display: "inline-block", animation: "blink 1.4s ease-in-out infinite" }} />
            LIVE
          </div>
        </div>
      </div>{/* end flex row */}

      {/* Portfolio history chart — inside the hero card, only when value is set */}
      {portfolioReady && <PortfolioHistoryChart />}

      </div>{/* end hero card */}

      {/* ── 5 Metrics row ────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <MetricCard
          label="TODAY PROFIT/LOSS"
          value={dayChange != null ? `${isPositiveDay ? "+" : ""}$${Math.abs(dayChange).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—"}
          color={dayChange != null ? (isPositiveDay ? "var(--positive)" : "var(--negative)") : "var(--text-secondary)"}
          tooltip="How much money you made or lost across all your holdings today."
        />
        <MetricCard
          label="YEARLY RETURN"
          value={metrics?.ret ? `${metrics.ret}%` : "—"}
          color={metrics?.ret && parseFloat(metrics.ret) >= 0 ? "var(--positive)" : "var(--negative)"}
          tooltip="How much your portfolio would grow in a year if it kept performing at its current pace. Estimated from your holdings' historical data."
        />
        <MetricCard
          label="RISK/RETURN RATIO"
          value={metrics?.sharpe ?? "—"}
          color={metrics?.sharpe && parseFloat(metrics.sharpe) >= 1 ? "var(--positive)" : "var(--caution)"}
          tooltip="How much return you're getting for each unit of risk you're taking. Above 1.0 is good — it means you're being rewarded well for the risk. Above 2.0 is excellent."
        />
        <MetricCard
          label="PRICE SWINGS"
          value={metrics?.vol ? `${metrics.vol}%` : "—"}
          color="var(--caution)"
          tooltip="How much your portfolio value tends to swing up and down. Lower = more stable. Higher = bigger swings in both directions."
          riskLabel={
            metrics?.vol == null ? undefined :
            metrics.vol < 15 ? "LOW" :
            metrics.vol <= 25 ? "MEDIUM" : "HIGH"
          }
        />
        <MetricCard
          label="GLOBAL RISK SCORE"
          value={geoScore !== null ? `${geoScore}/100` : "—"}
          color={geoScore >= 60 ? "var(--negative)" : geoScore >= 30 ? "var(--caution)" : "var(--positive)"}
          accent={geoScore !== null && geoScore >= 30}
          tooltip="How exposed your holdings are to political instability, conflicts, and sanctions around the world. 0 = low exposure, 100 = high exposure."
        />
        <MetricCard
          label="HOLDINGS"
          value={holdings.length}
          sub={`${totalWeight.toFixed(1)}% allocated`}
        />
      </div>

      {/* ── Middle row ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 14, marginBottom: 14 }}>

        {/* Holdings list */}
        <div style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
        }}>
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", letterSpacing: ".12em" }}>
              HOLDINGS — {holdings.length} POSITIONS
            </span>
            <button
              onClick={() => setActiveTab("builder")}
              style={{
                background: "transparent", border: "1px solid var(--border)",
                color: "var(--text-secondary)", padding: "3px 10px",
                borderRadius: "var(--radius-sm)", cursor: "pointer",
                fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: ".06em",
              }}
            >
              MANAGE →
            </button>
          </div>
          {!hasHoldings ? (
            <EmptyPortfolio onAddHolding={() => setActiveTab("builder")} />
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {holdings.slice(0, 12).map((h, i, arr) => {
                const changePct = h.liveChangePct ?? 0;
                const isPos     = changePct >= 0;
                const price     = h.livePrice ?? h.price;
                const flashInfo = flashedTickers[h.ticker];
                return (
                  <div key={h.ticker} className={`${flashInfo ? 'geo-flash' : ''}`.trim()} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: compact ? "5px 16px" : "9px 16px",
                    background: i % 2 === 0 ? "var(--surface)" : "var(--surface-2)",
                    borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none",
                    borderLeft: flashInfo ? `2px solid var(--caution)` : `2px solid var(--accent-dim)`,
                  }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)", fontWeight: 700, width: 52, flexShrink: 0 }}>
                      {h.ticker}
                    </span>
                    {flashInfo && (
                      <span className="geo-badge">{flashInfo.zone}</span>
                    )}
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 11, color: "var(--text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {h.liveName ?? h.name ?? h.ticker}
                    </span>
                    {price && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-primary)", flexShrink: 0 }}>
                        ${typeof price === "number" ? price.toFixed(2) : price}
                      </span>
                    )}
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
                      color: isPos ? "var(--positive)" : "var(--negative)",
                      flexShrink: 0, width: 60, textAlign: "right",
                    }}>
                      {isPos ? "↑" : "↓"} {Math.abs(changePct).toFixed(2)}%
                    </span>
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 10,
                      color: "var(--text-tertiary)",
                      width: 42, textAlign: "right", flexShrink: 0,
                    }}>
                      {(h.weight ?? 0).toFixed(1)}%
                    </span>
                    {h.sparkline?.length > 1 && (
                      <Sparkline data={h.sparkline} color={isPos ? "var(--positive)" : "var(--negative)"} width={60} height={24} />
                    )}
                  </div>
                );
              })}
              {holdings.length > 12 && (
                <div
                  onClick={() => setActiveTab("builder")}
                  style={{
                    padding: "8px 16px", textAlign: "center", cursor: "pointer",
                    borderTop: "1px solid var(--border)",
                    fontFamily: "var(--font-mono)", fontSize: 10,
                    color: "var(--accent)", letterSpacing: ".06em",
                    background: "var(--surface-2)",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--accent-dim)"}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--surface-2)"}
                >
                  +{holdings.length - 12} more · VIEW ALL IN BUILDER →
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Forex positions strip ─────────────────────── */}
        {forexPositions.length > 0 && (() => {
          const enriched = forexPositions.map(pos => {
            const q = stockData?.getQuote?.(pos.pair);
            const cur = q?.price ?? null;
            if (!cur) return { ...pos, pnl: null, pnlPct: null };
            const sign = pos.direction === "LONG" ? 1 : -1;
            const pnlPct = +(sign * (cur - pos.entryRate) / pos.entryRate * 100).toFixed(2);
            return { ...pos, currentRate: cur, pnl: +(pos.notional * pnlPct / 100).toFixed(2), pnlPct };
          });
          const totalPnl = enriched.reduce((s, p) => s + (p.pnl ?? 0), 0);
          return (
            <div style={{
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)", overflow: "hidden",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 16px", borderBottom: "1px solid var(--border)",
                background: "var(--surface-2)",
              }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: ".12em", color: "var(--text-tertiary)" }}>
                  FOREX POSITIONS — {enriched.length}
                </span>
                <span
                  onClick={() => setActiveTab("fxmarkets")}
                  style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--accent)", cursor: "pointer", letterSpacing: ".06em" }}
                >
                  MANAGE →
                </span>
              </div>
              {enriched.map((pos, i) => {
                const isPos = (pos.pnl ?? 0) >= 0;
                return (
                  <div key={pos.id} style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "8px 16px",
                    borderBottom: i < enriched.length - 1 ? "1px solid var(--border)" : "none",
                    borderLeft: `2px solid ${isPos ? "var(--positive)" : "var(--negative)"}44`,
                  }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)", fontWeight: 700, width: 64, flexShrink: 0 }}>
                      {pos.label}
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: pos.direction === "LONG" ? "var(--positive)" : "var(--negative)", width: 48, flexShrink: 0 }}>
                      {pos.direction === "LONG" ? "↑ LONG" : "↓ SHORT"}
                    </span>
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 10, color: "var(--text-tertiary)", flex: 1 }}>
                      Entry {pos.entryRate?.toFixed(4)}
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: isPos ? "var(--positive)" : "var(--negative)", flexShrink: 0 }}>
                      {pos.pnl != null ? `${isPos ? "+" : ""}$${Math.abs(pos.pnl).toFixed(0)}` : "—"}
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: isPos ? "var(--positive)" : "var(--negative)", width: 52, textAlign: "right", flexShrink: 0 }}>
                      {pos.pnlPct != null ? `${isPos ? "+" : ""}${pos.pnlPct.toFixed(2)}%` : "—"}
                    </span>
                  </div>
                );
              })}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 16px", borderTop: "1px solid var(--border)",
                background: "var(--surface-2)",
              }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".08em" }}>TOTAL FX P&L</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: totalPnl >= 0 ? "var(--positive)" : "var(--negative)" }}>
                  {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Right panel — geo-risk widget + portfolio metrics */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Geo-risk widget */}
          <div style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            padding: "16px",
            flex: 1,
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", marginBottom: 12 }}>
              GEO-RISK MONITOR
            </div>
            {geoScore !== null ? (
              <>
                {/* Score gauge */}
                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 32, fontWeight: 700, color: geoScore >= 60 ? "var(--negative)" : geoScore >= 30 ? "var(--caution)" : "var(--positive)" }}>
                    {geoScore}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".1em" }}>/ 100 SCORE</div>
                  <div style={{ height: 4, background: "var(--surface-3)", borderRadius: 2, margin: "10px 0", overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${geoScore}%`,
                      background: geoScore >= 60 ? "var(--negative)" : geoScore >= 30 ? "var(--caution)" : "var(--positive)",
                      borderRadius: 2, transition: "width .5s ease",
                    }} />
                  </div>
                </div>
                {/* Top exposed holdings */}
                {holdings.slice(0, 4).map(h => (
                  <div key={h.ticker} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--accent)" }}>{h.ticker}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-secondary)" }}>{(h.weight ?? 0).toFixed(1)}%</span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ textAlign: "center", color: "var(--text-tertiary)", fontFamily: "var(--font-body)", fontSize: 12, padding: "12px 0" }}>
                Add holdings to calculate geo-risk score
              </div>
            )}
            <button
              onClick={() => setActiveTab("world")}
              style={{
                width: "100%", marginTop: 12,
                background: "var(--accent-dim)", border: "1px solid var(--accent-mid)",
                color: "var(--accent)", padding: "8px 0",
                borderRadius: "var(--radius-sm)", cursor: "pointer",
                fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".08em",
              }}
            >
              OPEN GEO-RISK MAP →
            </button>
          </div>

          {/* Portfolio summary */}
          <div style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            padding: "16px",
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", marginBottom: 10 }}>
              PORTFOLIO METRICS
            </div>
            {[
              { label: "PRICE SWINGS",   value: metrics?.vol ? `${metrics.vol}%` : "—" },
              { label: "MARKET SENS.",   value: metrics?.beta ?? "—" },
              { label: "DIVIDEND YIELD", value: metrics?.divYield ? `${metrics.divYield}%` : "—" },
            ].map(m => (
              <div key={m.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".1em" }}>{m.label}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-primary)", fontWeight: 700 }}>{m.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sector breakdown ──────────────────────────────────── */}
      {hasHoldings && sectorWeights.length > 0 && (
        <div style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)", padding: "16px", marginBottom: 14,
        }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", marginBottom: 14 }}>
            SECTOR BREAKDOWN
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sectorWeights.map(([sector, weight], i) => {
              const SECTOR_COLORS = ["#3ddc84","#00c8ff","#ffd700","#ff6b35","#c084fc","#f472b6"];
              const col = SECTOR_COLORS[i % SECTOR_COLORS.length];
              return (
                <div key={sector}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-secondary)", letterSpacing: ".04em" }}>{sector}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: col, fontWeight: 700 }}>{weight.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 3, background: "var(--surface-3)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(weight, 100)}%`, background: col, borderRadius: 2, transition: "width .4s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Quick actions ─────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <QuickActionButton label="OPTIMIZER" icon="◈" desc="Automatically find the best split between your holdings to balance return and risk" onClick={() => setActiveTab("optimizer")} locked={!hasHoldings} onUnlock={() => setActiveTab("builder")} />
        <QuickActionButton label="CHECK VALUATIONS" icon="◉" desc="See whether each of your stocks is trading above or below its estimated fair value" onClick={() => setActiveTab("valuation")} />
        <QuickActionButton label="GEO-RISK MAP" icon="⊕" desc="See which countries your investments are exposed to and track active global conflicts" onClick={() => setActiveTab("world")} />
        <QuickActionButton label="BACKTEST" icon="▤" desc="See how your current portfolio would have performed over the last 10 years of real market data" onClick={() => setActiveTab("backtest")} locked={!hasHoldings} onUnlock={() => setActiveTab("builder")} />
        <QuickActionButton label="SCENARIO SIMULATOR" icon="⟳" desc="Run thousands of simulations to see the range of possible outcomes for your portfolio over 10 years" onClick={() => setActiveTab("montecarlo")} locked={!hasHoldings} onUnlock={() => setActiveTab("builder")} />
        <QuickActionButton label="COMPANY VALUATION" icon="◧" desc="Estimate how much a company is actually worth based on its future earnings — compare to its current price" onClick={() => setActiveTab("dcf")} locked={!hasHoldings} onUnlock={() => setActiveTab("builder")} />
      </div>

    </div>
  );
}
